import { Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigStorage, CrudRowsItemComponent, CrudRowsService, CrudRowsTypeInfo, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { ASSET_METADATA, ASSET_TYPE, Asset, AssetMaintenance, RefCode } from 'assets-model';
import { AssetService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class AssetMaintenanceRowsComponentBase<G> extends CrudRowsItemComponent<AssetMaintenance, number, G> implements OnInit {


  defaultSort = null;
  defaultSortDirection = 'asc';
  displayedColumns: string[] = [ 'select', 'assetMaintenanceId', 'asset', 'assetId', 'comments', 'createdBy', 'createdDate', 'lockVersion', 'maintenanceBy', 'maintenanceDate', 'maintenanceType', 'actions'];

  protected systemConfig: any;
  
  rowsSelectedByButton: boolean = true;
  
  public form: FormGroup;
  
  assetSelectionList: Asset[];

  maintenanceTypeSelectionList: RefCode[];


  constructor(typeInfo: CrudRowsTypeInfo<AssetMaintenance, number, G>,
    protected assetMaintenanceService: CrudRowsService<AssetMaintenance, number, G>,
    protected assetService: AssetService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected configStorage: ConfigStorage,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar,
    defaultRowCount?: number) {
    super(typeInfo, fb, elementRef, router, route, configStorage, userStorage, dialog, assetMaintenanceService, injector, snackbar, defaultRowCount);
  }

  createFormRow(row: AssetMaintenance): FormGroup {
    return this.fb.group({
      assetMaintenanceId: [row.assetMaintenanceId, []],
      asset: [row.asset, [requiredValidator, ]],
      assetId: [row.assetId, [requiredValidator, ]],
      comments: [row.comments, [requiredValidator, ]],
      createdBy: [row.createdBy, []],
      createdDate: [row.createdDate, []],
      lockVersion: [row.lockVersion, []],
      maintenanceBy: [row.maintenanceBy, [requiredValidator, ]],
      maintenanceDate: [row.maintenanceDate, [requiredValidator, ]],
      maintenanceType: [row.maintenanceType, [requiredValidator, ]]
    });
  }

  createNewItem(): AssetMaintenance {
    return new AssetMaintenance();
  }

  sortingDataAccessor(item: AssetMaintenance, columnDef: string) {
    // important to define these as adding when new rows, nulls MUST be sorted to the bottom
    // or the form controls will break
    switch (columnDef) {
      case 'assetMaintenanceId':
        if (item?.assetMaintenanceId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.assetMaintenanceId;
        }
      case 'asset':
        if (item?.asset?.assetId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.asset.assetId;
        }
      case 'assetId':
        if (item?.assetId == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.assetId;
        }
      case 'comments':
        if (item?.comments == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.comments;
        }
      case 'createdBy':
        if (item?.createdBy == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.createdBy;
        }
      case 'createdDate':
        if (item?.createdDate == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.createdDate;
        }
      case 'lockVersion':
        if (item?.lockVersion == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.lockVersion;
        }
      case 'maintenanceBy':
        if (item?.maintenanceBy == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.maintenanceBy;
        }
      case 'maintenanceDate':
        if (item?.maintenanceDate == null) {
          return this.sort.direction == "asc" ? Number.MAX_VALUE : Number.MIN_VALUE;
        } else {
          return item.maintenanceDate;
        }
      case 'maintenanceType':
        if (item?.maintenanceType == null) {
          return this.sort.direction == "asc" ? 'Ω' : ' ';
        } else {
          return item.maintenanceType;
        }
      default:
        return item[columnDef];
    }
  }
  
  
  ngOnInit() {
    super.ngOnInit();
    
    function initOptions(config: any): any {
      config = config || {};
      return config;
    }
    this.systemConfig = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.systemConfig = initOptions(config);
    });
  }
  
  setDefaults() {
    if (!this.readOnly) {

      this.userStorage.nextNonNullItem().subscribe(userDetails => {
      
        // re-trigger updating drop-downs.
        this.rowsSet();
      });
    }
    this.setDefaultDisabledFields();
  }

  rowsSet() {
    super.rowsSet();
    if (this.selectedRowForm()) {
      this.selectedRowForm().markAsPristine();
    }
  }
  
  filtersDisabled() {
    return (this.selectedRow != null && this.isNewRow(this.selectedRow)) || this.selectedForm?.invalid || this.unsavedChanges();
  }


  dataSourceFilter(dataSource: AssetMaintenance[]): AssetMaintenance[] {
    return dataSource;
  }
  
  selectedRowChanged(row: AssetMaintenance): void {
    super.selectedRowChanged(row);
    this.resetSelectionListSubscriptionsForRow(row);
  }

  protected resetSelectionListSubscriptionsForRow(row: AssetMaintenance): void {
  }

  assetMaintenanceIdRowChanging(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  assetMaintenanceIdRowChanged(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetMaintenanceIdRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  assetMaintenanceIdRowChangedByUser(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  assetDisplay(item: Asset) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ASSET_TYPE.description(item);
  }
  
  assetLoadSelectionList() {
    this.assetService.findAll().subscribe(
      data => {
        this.assetSelectionList = ASSET_TYPE.entityDecorator.ensureMultiContained(this.rows && this.rows.map(r => r.asset), data.items);
      }
    );
  }

  assetRowChanging(value: Asset, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  assetRowChanged(value: Asset, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  assetRowChangedByUser(value: Asset, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  assetCompare(a: any, b: any) {
    return ASSET_METADATA.equals(a, b);
  }
  
  assetIdRowChanging(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  assetIdRowChanged(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.assetIdRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  assetIdRowChangedByUser(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  commentsRowChanging(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  commentsRowChanged(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.commentsRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  commentsRowChangedByUser(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  createdByRowChanging(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  createdByRowChanged(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  createdByRowChangedByUser(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  createdDateRowChanging(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  createdDateRowChanged(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  createdDateRowChangedByUser(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  lockVersionRowChanging(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  lockVersionRowChanged(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  lockVersionRowChangedByUser(value: number, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  maintenanceByRowChanging(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  maintenanceByRowChanged(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceByRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  maintenanceByRowChangedByUser(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  maintenanceDateRowChanging(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  maintenanceDateRowChanged(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceDateRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  maintenanceDateRowChangedByUser(value: Date, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  
  maintenanceTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('MAINTENANCE_TYPE').subscribe(
      data => {
        this.maintenanceTypeSelectionList = data;
      }
    );
  }

  maintenanceTypeRowChanging(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {}
  
  maintenanceTypeRowChanged(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateRowsSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.maintenanceTypeRowChangedByUser(value, element, index, unsetDependents);
    }
  }  
  
  maintenanceTypeRowChangedByUser(value: string, element: AssetMaintenance, index: number, unsetDependents: boolean = true) {
  }
  


  assetMaintenanceIdRow(index: number) {
    return this.rowControl('assetMaintenanceId', index);
  }
  
  assetRow(index: number) {
    return this.rowControl('asset', index);
  }
  
  assetIdRow(index: number) {
    return this.rowControl('assetId', index);
  }
  
  commentsRow(index: number) {
    return this.rowControl('comments', index);
  }
  
  createdByRow(index: number) {
    return this.rowControl('createdBy', index);
  }
  
  createdDateRow(index: number) {
    return this.rowControl('createdDate', index);
  }
  
  lockVersionRow(index: number) {
    return this.rowControl('lockVersion', index);
  }
  
  maintenanceByRow(index: number) {
    return this.rowControl('maintenanceBy', index);
  }
  
  maintenanceDateRow(index: number) {
    return this.rowControl('maintenanceDate', index);
  }
  
  maintenanceTypeRow(index: number) {
    return this.rowControl('maintenanceType', index);
  }
  
}





